import React, { createRef, Fragment, Suspense } from "react";
import { Route, Redirect } from "react-router-dom";
import { ApmRoute } from "@elastic/apm-rum-react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { APP_MODE } from "../../../config/constants";
import ErrorBoundary from "./ErrorBoundary";
import SideMenu from "./SideMenu";
import TopBar from "./TopBar";
import { onMessageListener } from "../../../config/firebase";
import ToastMsg from "./ToastMessage";
import notificationActions from "../../notification/actions";
import dashboardActions from "../../dashboard/actions";
import userActions from "../../users/actions";
import { resolveSessionDefaultProject } from "../../../utils/dashboardSession";
import { canUseOttoPage, hydrateOttoAccess, subscribeOttoAccess } from "../../filterAssistant/core/ottoAccess";
import PageNotFound from "./PageNotFound";
import ScreenshotModal from "./ScreenshotModal";
import LoadingOverlay from "react-loading-overlay";
import Loader from "./Loader";
import domtoimage from "dom-to-image";
import ReactTooltip from "react-tooltip";
import { GlossaryTooltip } from "../../../utils/glossaryHelper/GlossaryTooltip";

class PrivateRoute extends React.Component {
    state = {
        showNav: true,
        isScreenshotModalOpen: false,
        screenshot: null,
        loading: false,
        svgString: null
    };

    componentDidMount = () => {
        const redirectAfterLoginRoutes = [
            "/project-capital-request/:section/:id",
            "/assetmanagement/:section/:id/:tab",
            "/recommendations/:section/:id/:tab"
        ];
        if (!localStorage.getItem("userId") && redirectAfterLoginRoutes.includes(this.props.path)) {
            const location = this.props?.location;
            if (location) {
                const { pathname = "", search = "", hash = "" } = location;
                localStorage.setItem("prevRoute", `${pathname}${search}${hash}`);
            }
        }

        if (localStorage.getItem("userId")) {
            resolveSessionDefaultProject({
                getUserById: this.props.getUserById,
                updateDefaultProject: this.props.updateDefaultProject,
                reduxDefaultProject: this.props.dashboardReducer?.defaultProject
            });
        }

        window.addEventListener("keydown", this.handleKeyDown);
        const badge = document.querySelector(".grecaptcha-badge");
        if (badge) {
            badge.style.display = "none";
        }

        // Otto's access grants (who, beyond super_admin, may see the assistant and its AI Settings pages)
        // live in Otto's own backend and are fetched once per session. Hydrating HERE — the shell that
        // wraps every private page — means one subscription covers the side menu and all three of Otto's
        // mount sites: when the answer lands, this re-renders and every `canUseOttoPage` call below it
        // sees the real grant instead of the pre-hydration super_admin-only fallback.
        if (localStorage.getItem("userId")) {
            this.unsubscribeOttoAccess = subscribeOttoAccess(() => {
                if (this.mountedRef) this.setState({ ottoAccessVersion: (this.state.ottoAccessVersion || 0) + 1 });
            });
            this.mountedRef = true;
            hydrateOttoAccess();
        }
    };

    componentDidUpdate = prevProps => {
        const windowWidth = window.innerWidth;
        const sidebarElement = document.getElementById("sidebar");
        if (prevProps.path !== this.props.path) {
            if (windowWidth < 991 && sidebarElement) {
                sidebarElement.classList.add("expandnavbar");
            }
        }
    };

    captureScreenshot = async () => {
        try {
            this.setState({ loading: true });
            const node = document.getElementById("capture-area");
            if (!node) return;

            const svgString = await domtoimage.toSvg(node, {
                bgcolor: "transparent",
                cacheBust: true,
                filter: node => {
                    if (node.tagName === "IMG") {
                        return false;
                    }
                    if (node.id === "map-canvas") return false;
                    return true;
                }
            });

            this.setState({
                svgString,
                isScreenshotModalOpen: true
            });
        } catch (error) {
            console.error("Error capturing SVG screenshot:", error);
        } finally {
            this.setState({ loading: false });
        }
    };

    componentWillUnmount() {
        window.removeEventListener("keydown", this.handleKeyDown);
        this.mountedRef = false;
        if (this.unsubscribeOttoAccess) this.unsubscribeOttoAccess();
    }

    handleKeyDown = event => {
        if (event.altKey && event.key === "s") {
            event.preventDefault();
            this.captureScreenshot();
        }
    };

    handleShowNa = () => {
        this.setState({
            showNav: !this.state.showNav
        });
    };

    checkUserRole = roles => {
        if (!roles || roles.length === 0) return true;
        const userRole = localStorage.getItem("role");
        return roles.includes(userRole);
    };

    // Route gate for Otto's own pages. Distinct from `roles` because Otto's access is a GRANT, not a role:
    // super_admin always passes, and any other user passes only if they were given the page on the Otto
    // User Permissions screen. Central policy lives in filterAssistant/core/ottoAccess.
    checkOttoPage = ottoPage => {
        if (!ottoPage) return true;
        return canUseOttoPage(ottoPage);
    };

    render() {
        const { component: Component, roles, ottoPage, ...rest } = this.props;
        onMessageListener()
            .then(payload => {
                this.props.getUnreadNotifications({ force: true });
                if (payload?.notification?.title) {
                    ToastMsg({ title: payload.notification.title, body: payload.notification.body });
                }
            })
            .catch(err => console.log("failed: ", err));

        const isCrUser = localStorage.getItem("infrastructure_request_user") === "yes";

        return (
            <ApmRoute
                {...rest}
                render={props => {
                    if (!localStorage.getItem("userId")) {
                        return <Redirect to={{ pathname: "/" }} />;
                    }

                    if (!this.checkUserRole(roles) || !this.checkOttoPage(ottoPage)) {
                        return <PageNotFound />;
                    }

                    const isDashboardShell =
                        props.match.path === "/dashboard" || props.match.path === "/embedded-dashboard" || props.match.path === "/assetdashboard";

                    return (
                        <Fragment>
                            {isCrUser ? (
                                <>
                                    {props.match.params.tab === "cr-recommendations" ||
                                    props.match.path === "/recommendations/:section" ||
                                    props.match.path === "/recommendations/:section/:id" ||
                                    props.match.path === "/recommendations/:section/:id/:tab" ||
                                    props.match.path === "/documents/:section" ||
                                    props.match.path === "/documents/:section/:id" ||
                                    props.match.path === "/documents/:section/:id/:tab" ? (
                                        <div className="dashboard-outer">
                                            <div className={`outer-detail outer-height`}>
                                                <div className={`right-panel-section expandnavbarr`} id="main">
                                                    <div className="outer-main-zm">
                                                        <TopBar
                                                            handleShowNa={this.handleShowNa}
                                                            showNav={true}
                                                            hasDefaultProjectMenu={false}
                                                            isCrHeader
                                                        />
                                                        <ErrorBoundary>
                                                            <Suspense fallback={<></>}>
                                                                <Component {...props} />
                                                            </Suspense>
                                                        </ErrorBoundary>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <PageNotFound isCR />
                                    )}
                                </>
                            ) : props.match.path === "/home" ? (
                                <ErrorBoundary>
                                    <Suspense fallback={<></>}>
                                        <Component {...props} />
                                    </Suspense>
                                </ErrorBoundary>
                            ) : (
                                <LoadingOverlay active={this.state.loading} spinner={<Loader />} fadeSpeed={10}>
                                    <div className="dashboard-outer" id="capture-area">
                                        <ScreenshotModal
                                            show={this.state.isScreenshotModalOpen}
                                            handleClose={() => this.setState({ isScreenshotModalOpen: false })}
                                            imageSrc={this.state.screenshot}
                                            rawSvgDataUrl={this.state.svgString}
                                        />
                                        <div
                                            className={`outer-detail drop-nav ${
                                                isDashboardShell ? "dash-side-nav" : ""
                                            }`}
                                        >
                                            <SideMenu handleShowNa={this.handleShowNa} showNav={this.state.showNav} />
                                            <div
                                                className={`right-panel-section ${APP_MODE === "training" ? "right-common-blue-outer" : ""}`}
                                                id="main"
                                            >
                                                <div className="outer-main-zm">
                                                    {isDashboardShell ? null : (
                                                        <TopBar
                                                            handleShowNa={this.handleShowNa}
                                                            showNav={this.state.showNav}
                                                            hasDefaultProjectMenu={true}
                                                            captureScreenshot={this.captureScreenshot}
                                                        />
                                                    )}
                                                    <ErrorBoundary>
                                                        <Suspense fallback={<></>}>
                                                            <Component {...props} captureScreenshot={this.captureScreenshot} />
                                                        </Suspense>
                                                    </ErrorBoundary>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </LoadingOverlay>
                            )}
                            <GlossaryTooltip />
                        </Fragment>
                    );
                }}
            />
        );
    }
}

const mapStateToProps = state => ({
    dashboardReducer: state.dashboardReducer
});

export default withRouter(
    connect(mapStateToProps, {
        ...notificationActions,
        getUserById: userActions.getUserById,
        updateDefaultProject: dashboardActions.updateDefaultProject
    })(PrivateRoute)
);
